/**
 * Pure resolution of the browser tab title from route state.
 *
 * Extracted from `PageTitleManager` so the branch table can be exercised without a router or a
 * query client. The component's only remaining job is to feed this the route and the two names it
 * may need — every "which title wins" decision lives here.
 *
 * ⚠️ Order is behaviour: the branches are checked top to bottom and the first match returns, so
 * `/admin/companies/:id` must be tested before the generic `/admin/` prefix. Anything unmatched
 * falls through to `'aivo'` — a missing branch is silent, it does not throw.
 *
 * Titles are hardcoded English and deliberately bypass i18next (unchanged by the migration that
 * moved this file). German users get English tab titles; routing them through i18next is a
 * behaviour change and needs its own commit plus paired `en`/`de` keys.
 */

/** The subset of `useParams` this resolver actually reads. */
export interface PageTitleRouteParams {
  id?: string;
  path?: string;
  type?: string;
}

export interface PageTitleInput {
  /** `location.pathname` */
  pathname: string;
  /** `location.search` — read only on `/company/settings`, for the `tab` param. */
  search: string;
  params: PageTitleRouteParams;
  /** `audit.company.name`, once the audit query for `/dashboard/:id` has resolved. */
  auditCompanyName?: string;
  /** `audit.name`, once the audit query for `/dashboard/:id` has resolved. */
  auditName?: string;
  /**
   * The organisation's name, once the company query for a company / platform-admin route has
   * resolved. A platform-admin route reads it from the superadmin record, not `GET /company/:id`.
   */
  companyName?: string;
}

/**
 * Which company query a company page needs: `/admin/companies/:type/:id/...` reads
 * `@shared/api/superadminCompany` (its `:id` is not always a company id), every other company page
 * `GET /company/:id`. Exactly one of the two may be enabled per pathname.
 */
export const isPlatformAdminCompanyPathname = (pathname: string): boolean =>
  pathname.startsWith('/admin/companies/');

/**
 * Whether the title for this pathname is driven by a company query rather than the audit query.
 *
 * `PageTitleManager` uses this to pick which requests to enable, so it has to stay the single
 * definition of "company page" — a branch below that assumed a different answer than the
 * component fired a request for would render a title from data nobody fetched.
 */
export const isCompanyPathname = (pathname: string): boolean =>
  pathname.startsWith('/company/') || isPlatformAdminCompanyPathname(pathname);

export const getPageTitle = ({
  pathname,
  search,
  params,
  auditCompanyName,
  auditName,
  companyName,
}: PageTitleInput): string => {
  const { id, path, type } = params;

  // Dashboard routes - when viewing an audit
  if (pathname.startsWith('/dashboard/') && id && auditCompanyName && auditName) {
    return `${auditCompanyName} - ${auditName}`;
  }

  // Admin platform routes - global level views
  if (pathname === '/audits') {
    return 'Audits - aivo';
  }

  if (pathname === '/notifications') {
    return 'Notifications - aivo';
  }

  if (pathname === '/account/settings') {
    return 'Account settings - aivo';
  }

  if (pathname === '/company/settings') {
    const tab = new URLSearchParams(search).get('tab');
    return tab === 'templates' ? 'Templates - aivo' : 'Company settings - aivo';
  }

  if (isPlatformAdminCompanyPathname(pathname) && type && companyName) {
    return `${companyName} - Platform Admin - aivo`;
  }

  if (pathname.startsWith('/admin/')) {
    return 'Platform Admin - aivo';
  }

  if (isCompanyPathname(pathname) && companyName) {
    // Company-specific routes with company name
    if (path === 'audits') {
      return `${companyName} - Audits - aivo`;
    }
    if (path === 'settings') {
      return `${companyName} - Settings - aivo`;
    }
  }

  // Default fallback
  return 'aivo';
};
